import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { DOCUMENT_SCAN_STAGES } from 'shared'

type Props = {
  /** Show the scan beam + stage caption over the preview. */
  scanning?: boolean
  /** Fixed caption; cycles through OCR stages when omitted. */
  caption?: string
  compact?: boolean
  className?: string
  /** The document preview. Falls back to a blank page skeleton. */
  children?: React.ReactNode
}

const STAGE_MS = 1400

/**
 * Document "page" with an OCR scan beam sweeping over it. Used while a
 * document is queued / being scanned and in the upload modal.
 */
const ScanPreview: React.FC<Props> = ({
  scanning = false,
  caption,
  compact = false,
  className,
  children
}) => {
  const [stage, setStage] = useState(0)

  useEffect(() => {
    if (!scanning || caption) return undefined
    const intervalId = setInterval(() => {
      setStage((s) => (s + 1) % DOCUMENT_SCAN_STAGES.length)
    }, STAGE_MS)
    return () => clearInterval(intervalId)
  }, [scanning, caption])

  return (
    <div
      className={classNames(
        'scan-preview',
        {
          'scan-preview--scanning': scanning,
          'scan-preview--compact': compact
        },
        className
      )}
    >
      {children ?? (
        <div className="scan-preview__paper" aria-hidden>
          <span className="scan-preview__line scan-preview__line--title" />
          <span className="scan-preview__line scan-preview__line--short" />
          <span className="scan-preview__line" />
          <span className="scan-preview__line" />
          <span className="scan-preview__table" />
          <span className="scan-preview__line scan-preview__line--short" />
          <span className="scan-preview__line scan-preview__line--total" />
        </div>
      )}
      {scanning && (
        <>
          <span className="scan-preview__beam" />
          {!compact && (
            <span className="scan-preview__caption" role="status">
              {caption ?? DOCUMENT_SCAN_STAGES[stage]}
            </span>
          )}
        </>
      )}
    </div>
  )
}

export default ScanPreview
